import React from 'react';
import { SubMenuItemModel } from './types.ts';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useSecuredDecoration } from '@components/security/securedDecoration';
import SvgCirclePlus from '@icons/circle-plus.svg?react';
import MenuBadges, { hasVisibleBadge } from './MenuBadges.tsx';
import MenuAttentionSheen from './MenuAttentionSheen.tsx';

interface ComponentProps extends SubMenuItemModel {
  /** Called when the user navigates from this item, so the hover overlay can close itself. */
  onNavigate?: () => void;
}

/**
 * A leaf row. Whether it is active is decided centrally in Menu (longest matching route wins), so
 * this component only renders — it no longer guesses from `location` with an exact path compare,
 * which never matched detail or nested routes.
 */
const SubMenuItem: React.FC<ComponentProps> = ({
  title,
  elementCount,
  onClick,
  onNavigate,
  active = false,
  create = false,
  url,
  dataTestId,
  secured,
  render,
  attention = false,
  content,
}) => {
  const decoration = useSecuredDecoration(secured);

  if (render === false) return null;
  if (content) return decoration.decorate(<div data-testid={dataTestId}>{content}</div>);

  const row = (
    <li
      className={twMerge(
        clsx(
          // `relative` + overflow-hidden anchor and clip the attention sheen.
          'relative overflow-hidden flex py-4 pr-4 pl-[60px] justify-between h-[48px] items-center w-[270px] transition-colors duration-150 motion-reduce:transition-none',
          // Only the section row carries the selected background; the item inside it is marked by
          // its brand-coloured text alone, so the highlighted block reads as one section rather
          // than two competing fills. Hover is the neutral grey used by the dropdown panels.
          'hover:bg-content-quaternary',
          { 'text-content-brand font-medium': active },
        ),
      )}
      onClick={e => {
        onClick?.(e);
        onNavigate?.();
      }}
    >
      <span className={'whitespace-nowrap'}>{title}</span>
      {hasVisibleBadge(elementCount) && (
        <span className={'flex items-center gap-1 shrink-0'}>
          <MenuBadges elementCount={elementCount} />
        </span>
      )}
      {/* Same colour and hover as RingilButton's borderless modes: brand by default, brandDark on
          hover, over a 200ms transition. */}
      {create && (
        <span
          // Same w-6 box as the sections' chevron, so the plus lines up with the carets above it.
          className={
            'w-6 shrink-0 flex items-center justify-center text-content-brand transition-all duration-200 motion-reduce:transition-none hover:text-complementary-brandDark'
          }
        >
          <SvgCirclePlus />
        </span>
      )}
      {attention && <MenuAttentionSheen />}
    </li>
  );

  return decoration.decorate(
    url ? (
      <Link to={url} className={twMerge(clsx('flex gap-4 items-center text-content-primary', { 'text-content-brand': active }))} data-testid={dataTestId}>
        {row}
      </Link>
    ) : (
      <div className={twMerge(clsx('flex gap-4 items-center text-content-primary', { 'text-content-brand': active }))} data-testid={dataTestId}>
        {row}
      </div>
    ),
  );
};

export default SubMenuItem;
